<template>
  <div class="modal fade" id="propertyDetailsModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            <i :class="getPropertyIcon(property.type)" class="me-2"></i>
            Property Details - {{ property.propertyId }}
          </h4>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <div class="row">
            <!-- Left Column - Property Info -->
            <div class="col-md-6">
              <div class="card border">
                <div class="card-header bg-primary text-white">
                  <h5 class="mb-0 text-white">
                    <i class="mdi mdi-information me-2"></i>
                    Property Information
                  </h5>
                </div>
                <div class="card-body">
                  <div class="d-flex align-items-center mb-4">
                    <div class="avatar-lg rounded-circle me-3" :class="getPropertyIconClass(property.type)">
                      <span class="avatar-title rounded-circle text-white fs-2">
                        <i :class="getPropertyIcon(property.type)"></i>
                      </span>
                    </div>
                    <div>
                      <h5 class="mb-1">{{ property.name }}</h5>
                      <p class="text-muted mb-0">{{ property.description }}</p>
                      <span class="badge" :class="getPropertyTypeClass(property.type)">
                        {{ formatPropertyType(property.type) }}
                      </span>
                    </div>
                  </div>

                  <table class="table table-borderless table-sm">
                    <tr>
                      <td class="fw-bold">Property ID:</td>
                      <td class="fw-bold text-primary">{{ property.propertyId }}</td>
                    </tr>
                    <tr>
                      <td class="fw-bold">Type:</td>
                      <td>{{ formatPropertyType(property.type) }}</td>
                    </tr>
                    <tr>
                      <td class="fw-bold">Size/Specifications:</td>
                      <td>{{ property.size }}</td>
                    </tr>
                    <tr>
                      <td class="fw-bold">Condition:</td>
                      <td>
                        <span class="badge" :class="getConditionClass(property.condition)">
                          {{ property.condition }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="fw-bold">Monthly Rate:</td>
                      <td class="fw-bold">{{ formatPrice(property.monthlyRate) }}</td>
                    </tr>
                    <tr>
                      <td class="fw-bold">Registration Date:</td>
                      <td>{{ property.registrationDate }}</td>
                    </tr>
                    <tr>
                      <td class="fw-bold">Last Updated:</td>
                      <td>{{ property.lastUpdated }}</td>
                    </tr>
                    <tr>
                      <td class="fw-bold">Status:</td>
                      <td>
                        <span class="badge" :class="getStatusClass(property.status)">
                          {{ property.status }}
                        </span>
                      </td>
                    </tr>
                  </table>
                </div>
              </div>

              <!-- Location Details -->
              <div class="card border mt-3">
                <div class="card-header bg-info text-white">
                  <h5 class="mb-0 text-white">
                    <i class="mdi mdi-map-marker me-2"></i>
                    Location Details
                  </h5>
                </div>
                <div class="card-body">
                  <table class="table table-borderless table-sm">
                    <tr>
                      <td class="fw-bold">Sub County:</td>
                      <td>{{ property.subCounty }}</td>
                    </tr>
                    <tr>
                      <td class="fw-bold">Ward:</td>
                      <td>{{ property.ward }}</td>
                    </tr>
                    <tr>
                      <td class="fw-bold">Specific Location:</td>
                      <td>{{ property.specificLocation }}</td>
                    </tr>
                  </table>

                  <div class="mt-3">
                    <button class="btn btn-outline-info btn-sm">
                      <i class="mdi mdi-map me-1"></i>
                      View on Map
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column - Allocation Info -->
            <div class="col-md-6">
              <div class="card border">
                <div class="card-header" :class="property.currentOccupant ? 'bg-success text-white' : 'bg-secondary text-white'">
                  <h5 class="mb-0 text-white">
                    <i class="mdi mdi-account me-2"></i>
                    {{ property.currentOccupant ? 'Current Allocation' : 'No Current Allocation' }}
                  </h5>
                </div>
                <div class="card-body">
                  <div v-if="property.currentOccupant">
                    <div class="d-flex align-items-center mb-3">
                      <div class="avatar-md rounded-circle bg-success me-3">
                        <span class="avatar-title rounded-circle bg-success text-white">
                          {{ property.currentOccupant.name.charAt(0).toUpperCase() }}
                        </span>
                      </div>
                      <div>
                        <h6 class="mb-1">{{ property.currentOccupant.name }}</h6>
                        <small class="text-muted">{{ property.currentOccupant.phone }}</small>
                      </div>
                    </div>

                    <table class="table table-borderless table-sm">
                      <tr>
                        <td class="fw-bold">Allocated Date:</td>
                        <td>{{ property.currentOccupant.allocatedDate }}</td>
                      </tr>
                      <tr v-if="property.currentOccupant.leaseExpiry">
                        <td class="fw-bold">Lease Expiry:</td>
                        <td>{{ property.currentOccupant.leaseExpiry }}</td>
                      </tr>
                      <tr v-if="property.currentOccupant.businessType">
                        <td class="fw-bold">Business Type:</td>
                        <td>{{ property.currentOccupant.businessType }}</td>
                      </tr>
                      <tr v-if="property.currentOccupant.designation">
                        <td class="fw-bold">Designation:</td>
                        <td>{{ property.currentOccupant.designation }}</td>
                      </tr>
                      <tr v-if="property.currentOccupant.project">
                        <td class="fw-bold">Project:</td>
                        <td>{{ property.currentOccupant.project }}</td>
                      </tr>
                      <tr v-if="property.currentOccupant.driver">
                        <td class="fw-bold">Driver:</td>
                        <td>{{ property.currentOccupant.driver }}</td>
                      </tr>
                      <tr v-if="property.currentOccupant.supervisor">
                        <td class="fw-bold">Supervisor:</td>
                        <td>{{ property.currentOccupant.supervisor }}</td>
                      </tr>
                      <tr>
                        <td class="fw-bold">Monthly Payment:</td>
                        <td class="fw-bold">{{ formatPrice(property.currentOccupant.monthlyPayment) }}</td>
                      </tr>
                    </table>

                    <div class="mt-3">
                      <div class="d-flex gap-2">
                        <button @click="terminateAllocation" class="btn btn-warning btn-sm">
                          <i class="mdi mdi-account-remove me-1"></i>
                          Terminate Allocation
                        </button>
                        <button @click="generateLeaseDocument" class="btn btn-info btn-sm">
                          <i class="mdi mdi-file-document me-1"></i>
                          Generate Lease Document
                        </button>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-center py-4">
                    <i class="mdi mdi-account-off text-muted" style="font-size: 4rem;"></i>
                    <h6 class="text-muted mt-2">Property Available for Allocation</h6>
                    <button @click="allocateProperty" class="btn btn-success mt-2">
                      <i class="mdi mdi-account-plus me-1"></i>
                      Allocate Property
                    </button>
                  </div>
                </div>
              </div>

              <!-- Financial Summary -->
              <div class="card border mt-3">
                <div class="card-header bg-warning text-dark">
                  <h5 class="mb-0">
                    <i class="mdi mdi-currency-usd me-2"></i>
                    Financial Summary
                  </h5>
                </div>
                <div class="card-body">
                  <div class="row text-center">
                    <div class="col-4">
                      <div class="border-end">
                        <h4 class="mb-1">{{ formatPrice(property.monthlyRate) }}</h4>
                        <small class="text-muted">Monthly Rate</small>
                      </div>
                    </div>
                    <div class="col-4">
                      <div class="border-end">
                        <h4 class="mb-1">{{ formatPrice(calculateYearlyRevenue()) }}</h4>
                        <small class="text-muted">Yearly Revenue</small>
                      </div>
                    </div>
                    <div class="col-4">
                      <h4 class="mb-1">{{ calculateUtilizationRate() }}%</h4>
                      <small class="text-muted">Utilization Rate</small>
                    </div>
                  </div>

                  <div class="mt-3">
                    <h6>Payment History (Last 6 Months)</h6>
                    <div class="payment-history">
                      <div v-for="payment in mockPaymentHistory" :key="payment.month"
                           class="d-flex justify-content-between align-items-center py-2 border-bottom">
                        <div>
                          <span class="fw-bold">{{ payment.month }}</span>
                          <small class="text-muted d-block">{{ payment.date }}</small>
                        </div>
                        <div class="text-end">
                          <span class="fw-bold">{{ formatPrice(payment.amount) }}</span>
                          <span class="badge ms-2" :class="payment.status === 'PAID' ? 'bg-success' : 'bg-danger'">
                            {{ payment.status }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Maintenance History -->
          <div class="row mt-4">
            <div class="col-12">
              <div class="card border">
                <div class="card-header bg-secondary text-white">
                  <h5 class="mb-0 text-white">
                    <i class="mdi mdi-wrench me-2"></i>
                    Maintenance & Activity History
                  </h5>
                </div>
                <div class="card-body">
                  <div class="timeline">
                    <div class="timeline-item">
                      <div class="timeline-marker bg-success"></div>
                      <div class="timeline-content">
                        <h6 class="mb-1">Property Allocated</h6>
                        <p class="mb-1">Property allocated to {{ property.currentOccupant?.name || 'Current Occupant' }}</p>
                        <small class="text-muted">{{ property.currentOccupant?.allocatedDate || '2024-01-15' }}</small>
                      </div>
                    </div>
                    <div class="timeline-item">
                      <div class="timeline-marker bg-info"></div>
                      <div class="timeline-content">
                        <h6 class="mb-1">Routine Maintenance</h6>
                        <p class="mb-1">Regular maintenance and inspection completed</p>
                        <small class="text-muted">2024-11-30</small>
                      </div>
                    </div>
                    <div class="timeline-item">
                      <div class="timeline-marker bg-warning"></div>
                      <div class="timeline-content">
                        <h6 class="mb-1">Property Updated</h6>
                        <p class="mb-1">Property details and rates updated</p>
                        <small class="text-muted">2024-10-15</small>
                      </div>
                    </div>
                    <div class="timeline-item">
                      <div class="timeline-marker bg-primary"></div>
                      <div class="timeline-content">
                        <h6 class="mb-1">Property Registered</h6>
                        <p class="mb-1">Property added to county registry</p>
                        <small class="text-muted">{{ property.registrationDate }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Documents Section -->
          <div class="row mt-4">
            <div class="col-12">
              <div class="card border">
                <div class="card-header bg-dark text-white">
                  <h5 class="mb-0 text-white">
                    <i class="mdi mdi-file-document me-2"></i>
                    Related Documents
                  </h5>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-3">
                      <div class="document-item p-3 border rounded text-center">
                        <i class="mdi mdi-file-pdf text-danger" style="font-size: 2rem;"></i>
                        <div class="mt-2">
                          <small class="fw-bold">Property Registration Certificate</small>
                          <div class="mt-1">
                            <button @click="downloadDocument('certificate')" class="btn btn-outline-primary btn-sm">Download</button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="document-item p-3 border rounded text-center">
                        <i class="mdi mdi-file-document text-primary" style="font-size: 2rem;"></i>
                        <div class="mt-2">
                          <small class="fw-bold">Lease Agreement</small>
                          <div class="mt-1">
                            <button @click="downloadDocument('lease')" class="btn btn-outline-primary btn-sm">Download</button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="document-item p-3 border rounded text-center">
                        <i class="mdi mdi-file-image text-success" style="font-size: 2rem;"></i>
                        <div class="mt-2">
                          <small class="fw-bold">Property Images</small>
                          <div class="mt-1">
                            <button @click="viewImages" class="btn btn-outline-primary btn-sm">View</button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="document-item p-3 border rounded text-center">
                        <i class="mdi mdi-file-chart text-info" style="font-size: 2rem;"></i>
                        <div class="mt-2">
                          <small class="fw-bold">Maintenance Reports</small>
                          <div class="mt-1">
                            <button @click="viewMaintenanceReports" class="btn btn-outline-primary btn-sm">View</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Additional Property Specifications -->
          <div v-if="property.type === 'VEHICLE' || property.type === 'MACHINERY'" class="row mt-4">
            <div class="col-12">
              <div class="card border">
                <div class="card-header bg-info text-white">
                  <h5 class="mb-0 text-white">
                    <i class="mdi mdi-car me-2"></i>
                    Vehicle/Machinery Specifications
                  </h5>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-3">
                      <strong>Make/Brand:</strong><br>
                      <span class="text-muted">{{ property.make || 'Toyota' }}</span>
                    </div>
                    <div class="col-md-3">
                      <strong>Model:</strong><br>
                      <span class="text-muted">{{ property.model || 'Hilux' }}</span>
                    </div>
                    <div class="col-md-3">
                      <strong>Year:</strong><br>
                      <span class="text-muted">{{ property.yearOfManufacture || '2020' }}</span>
                    </div>
                    <div class="col-md-3">
                      <strong>Registration:</strong><br>
                      <span class="text-muted">{{ property.registrationNumber || 'KCA 123A' }}</span>
                    </div>
                  </div>
                  <div class="row mt-3">
                    <div class="col-md-3">
                      <strong>Engine Number:</strong><br>
                      <span class="text-muted">{{ property.engineNumber || 'ENG123456' }}</span>
                    </div>
                    <div class="col-md-3">
                      <strong>Serial Number:</strong><br>
                      <span class="text-muted">{{ property.serialNumber || 'SN789456' }}</span>
                    </div>
                    <div class="col-md-3">
                      <strong>Mileage/Hours:</strong><br>
                      <span class="text-muted">{{ property.mileage || '45,000 km' }}</span>
                    </div>
                    <div class="col-md-3">
                      <strong>Last Service:</strong><br>
                      <span class="text-muted">{{ property.lastService || '2024-12-15' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="d-flex justify-content-between w-100">
            <div>
              <button @click="generateReport" class="btn btn-success me-2">
                <i class="mdi mdi-file-document me-1"></i>
                Generate Report
              </button>
              <button @click="scheduleMaintenance" class="btn btn-warning me-2">
                <i class="mdi mdi-wrench me-1"></i>
                Schedule Maintenance
              </button>
              <button v-if="!property.currentOccupant" @click="allocateProperty" class="btn btn-info">
                <i class="mdi mdi-account-plus me-1"></i>
                Allocate Property
              </button>
            </div>
            <div>
              <button @click="editProperty" class="btn btn-primary me-2">
                <i class="mdi mdi-pencil me-1"></i>
                Edit
              </button>
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from "moment";

export default {
  name: "PropertyDetailsModal",
  props: {
    property: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      mockPaymentHistory: [
        { month: 'January 2025', date: '2025-01-15', amount: 3500, status: 'PAID' },
        { month: 'December 2024', date: '2024-12-15', amount: 3500, status: 'PAID' },
        { month: 'November 2024', date: '2024-11-15', amount: 3500, status: 'PAID' },
        { month: 'October 2024', date: '2024-10-15', amount: 3500, status: 'PAID' },
        { month: 'September 2024', date: '2024-09-15', amount: 3500, status: 'PAID' },
        { month: 'August 2024', date: '2024-08-15', amount: 3500, status: 'OVERDUE' }
      ]
    }
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 0
      }).format(value || 0);
    },

    formatPropertyType(type) {
      const typeMap = {
        'MARKET_STALL': 'Market Stall',
        'COUNTY_HOUSE': 'County House',
        'MACHINERY': 'Machinery',
        'EQUIPMENT': 'Equipment',
        'VEHICLE': 'Vehicle',
        'LAND': 'Land'
      };
      return typeMap[type] || type;
    },

    getPropertyTypeClass(type) {
      switch (type) {
        case 'MARKET_STALL':
          return 'bg-primary';
        case 'COUNTY_HOUSE':
          return 'bg-success';
        case 'MACHINERY':
          return 'bg-warning';
        case 'EQUIPMENT':
          return 'bg-info';
        case 'VEHICLE':
          return 'bg-secondary';
        case 'LAND':
          return 'bg-dark';
        default:
          return 'bg-secondary';
      }
    },

    getPropertyIcon(type) {
      switch (type) {
        case 'MARKET_STALL':
          return 'mdi mdi-store';
        case 'COUNTY_HOUSE':
          return 'mdi mdi-home';
        case 'MACHINERY':
          return 'mdi mdi-truck';
        case 'EQUIPMENT':
          return 'mdi mdi-tools';
        case 'VEHICLE':
          return 'mdi mdi-car';
        case 'LAND':
          return 'mdi mdi-map';
        default:
          return 'mdi mdi-cube';
      }
    },

    getPropertyIconClass(type) {
      return this.getPropertyTypeClass(type);
    },

    getStatusClass(status) {
      switch (status) {
        case 'AVAILABLE':
          return 'bg-success';
        case 'ALLOCATED':
          return 'bg-warning';
        case 'MAINTENANCE':
          return 'bg-info';
        case 'DECOMMISSIONED':
          return 'bg-danger';
        default:
          return 'bg-secondary';
      }
    },

    getConditionClass(condition) {
      switch (condition?.toLowerCase()) {
        case 'excellent':
          return 'bg-success';
        case 'good':
          return 'bg-primary';
        case 'fair':
          return 'bg-warning';
        case 'poor':
          return 'bg-danger';
        default:
          return 'bg-secondary';
      }
    },

    calculateYearlyRevenue() {
      return (this.property.monthlyRate || 0) * 12;
    },

    calculateUtilizationRate() {
      // Mock calculation - in real app, this would be based on actual occupancy data
      return this.property.currentOccupant ? 100 : 0;
    },

    editProperty() {
      this.$emit('edit-property', this.property);

      // Close this modal
      const modalElement = document.getElementById('propertyDetailsModal');
      if (modalElement) {
        const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
        if (bootstrapModal) {
          bootstrapModal.hide();
        }
      }
    },

    allocateProperty() {
      this.$emit('allocate-property', this.property);

      // Close this modal
      const modalElement = document.getElementById('propertyDetailsModal');
      if (modalElement) {
        const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
        if (bootstrapModal) {
          bootstrapModal.hide();
        }
      }
    },

    terminateAllocation() {
      if (confirm(`Are you sure you want to terminate the allocation for ${this.property.propertyId}?`)) {
        this.$emit('terminate-allocation', this.property);
      }
    },

    generateReport() {
      console.log('Generating report for:', this.property.propertyId);
      alert(`Generating comprehensive report for ${this.property.propertyId}...`);
    },

    scheduleMaintenance() {
      console.log('Scheduling maintenance for:', this.property.propertyId);
      alert(`Scheduling maintenance for ${this.property.propertyId}...`);
    },

    generateLeaseDocument() {
      console.log('Generating lease document for:', this.property.propertyId);
      alert(`Generating lease document for ${this.property.propertyId}...`);
    },

    downloadDocument(type) {
      console.log('Downloading document:', type, 'for property:', this.property.propertyId);
      alert(`Downloading ${type} document for ${this.property.propertyId}...`);
    },

    viewImages() {
      console.log('Viewing images for:', this.property.propertyId);
      alert(`Opening image gallery for ${this.property.propertyId}...`);
    },

    viewMaintenanceReports() {
      console.log('Viewing maintenance reports for:', this.property.propertyId);
      alert(`Opening maintenance reports for ${this.property.propertyId}...`);
    }
  }
}
</script>

<style scoped>
.avatar-lg {
  width: 4rem;
  height: 4rem;
}

.avatar-md {
  width: 3rem;
  height: 3rem;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

.table-borderless td {
  border: none;
  padding: 0.5rem 0;
}

.timeline {
  position: relative;
  padding-left: 2rem;
}

.timeline-item {
  position: relative;
  margin-bottom: 1.5rem;
}

.timeline-item:not(:last-child):before {
  content: '';
  position: absolute;
  left: -1.5rem;
  top: 1.5rem;
  height: calc(100% + 1rem);
  width: 2px;
  background-color: #dee2e6;
}

.timeline-marker {
  position: absolute;
  left: -1.75rem;
  top: 0.25rem;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  border: 2px solid white;
}

.timeline-content {
  margin-left: 0.5rem;
}

.border-end {
  border-right: 1px solid #dee2e6 !important;
}

.modal-xl {
  max-width: 1200px;
}

.card-header.bg-primary h5,
.card-header.bg-success h5,
.card-header.bg-info h5,
.card-header.bg-secondary h5,
.card-header.bg-warning h5,
.card-header.bg-dark h5 {
  color: white !important;
}

.document-item {
  transition: all 0.3s ease;
  cursor: pointer;
}

.document-item:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  transform: translateY(-2px);
}

.payment-history {
  max-height: 200px;
  overflow-y: auto;
}

.border {
  border-color: #dee2e6 !important;
}
</style>